import { useState, useEffect } from "react";
import { Scale, Send, CheckCircle, AlertCircle, ChevronDown } from "lucide-react";
import { API, STUDIO_LOGO } from "../lib/constants";
import { useUser } from "../contexts/UserContext";
import AuthModal from "../components/AuthModal";
import WitnessStandTabs from "../components/WitnessStandTabs";

const FIELD_CONFIG = [
  {
    key: "what_happened",
    label: "What Happened?",
    required: true,
    helper: "Describe the situation in your own words. Min 50 characters.",
    placeholder: "The appointment was at a quiet house on the edge of town...",
    minLength: 50,
  },
  {
    key: "who_was_involved",
    label: "Who Was Involved?",
    required: true,
    helper: "No real names — describe the people by their roles or archetypes.",
    placeholder: "An elderly widow, her estranged son, and a nervous attorney...",
  },
  {
    key: "what_made_it_memorable",
    label: "What Made It Memorable?",
    required: true,
    placeholder: "The moment I realized the signature didn't match...",
  },
  {
    key: "how_did_it_end",
    label: "How Did It End?",
    required: true,
    placeholder: "I had to make a decision that changed everything...",
  },
  {
    key: "would_you_change",
    label: "Would You Change Anything?",
    required: false,
    placeholder: "If I could go back...",
  },
];

export default function WitnessStand() {
  const { user, refreshUser } = useUser();
  const [caseTypes, setCaseTypes] = useState([]);
  const [form, setForm] = useState({
    case_type: "",
    what_happened: "",
    who_was_involved: "",
    what_made_it_memorable: "",
    how_did_it_end: "",
    would_you_change: "",
    consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    fetch(`${API}/witness-stand/case-types`)
      .then((r) => r.ok ? r.json() : { case_types: [] })
      .then((d) => setCaseTypes(d.case_types || []))
      .catch(() => {});
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowAuth(true);
      return;
    }
    setError("");
    if (!form.case_type) { setError("Please select a case type."); return; }
    if (form.what_happened.length < 50) { setError("'What Happened' must be at least 50 characters."); return; }
    if (!form.who_was_involved.trim()) { setError("'Who Was Involved' is required."); return; }
    if (!form.what_made_it_memorable.trim()) { setError("'What Made It Memorable' is required."); return; }
    if (!form.how_did_it_end.trim()) { setError("'How Did It End' is required."); return; }
    if (!form.consent) { setError("You must agree to the terms."); return; }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("user_session_token");
      const res = await fetch(`${API}/witness-stand/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        let msg = "Submission failed";
        try { const d = await res.json(); msg = d.detail || msg; } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      setSuccess(data);
      refreshUser();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen pt-28 pb-20 px-6" data-testid="witness-stand-success">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-20 h-20 bg-green-600/10 border border-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-500" />
          </div>
          <h1 className="font-cinzel text-3xl font-bold mb-4">Case File Submitted</h1>
          <p className="text-gray-400 mb-2">Your testimony has been received.</p>
          <p className="text-lg font-bold text-amber-400 mb-8">+{success.credits_awarded} Credits Earned</p>
          <p className="text-sm text-gray-500 mb-8">New balance: {success.new_balance} credits</p>
          <button
            onClick={() => { setSuccess(null); setForm({ case_type: "", what_happened: "", who_was_involved: "", what_made_it_memorable: "", how_did_it_end: "", would_you_change: "", consent: false }); }}
            className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all"
            data-testid="submit-another-btn"
          >
            Submit Another Case File
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-6" data-testid="witness-stand-page">
      <div className="max-w-2xl mx-auto">
        <WitnessStandTabs />
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 bg-red-600/10 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Scale size={28} className="text-red-500" />
          </div>
          <p className="font-mono text-red-500 text-xs tracking-[0.3em] mb-2">SUBMIT TESTIMONY</p>
          <h1 className="font-cinzel text-3xl md:text-4xl font-bold mb-4">THE WITNESS STAND</h1>
          <p className="text-gray-400 text-sm max-w-lg mx-auto">
            Every notary has a story. Share yours — and if it inspires a future chapter of The Red Ink Chronicles, you'll earn{" "}
            <span className="text-amber-400 font-bold">10 Credits</span>.
          </p>
        </div>

        {/* Auth prompt */}
        {!user && (
          <div className="mb-8 p-5 bg-white/[0.03] border border-white/10 rounded-xl text-center" data-testid="witness-auth-prompt">
            <p className="text-gray-400 text-sm mb-2">You need an account to submit a case file.</p>
            <button
              onClick={() => setShowAuth(true)}
              className="text-red-400 hover:text-red-300 font-bold text-sm"
              data-testid="witness-signup-btn"
            >
              Sign Up Free
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Case Type */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Case Type <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select
                value={form.case_type}
                onChange={(e) => handleChange("case_type", e.target.value)}
                className="w-full appearance-none px-4 py-3 bg-[#1a1a1a] border border-white/15 rounded-xl text-sm text-white focus:outline-none focus:border-red-500 pr-10"
                data-testid="case-type-select"
              >
                <option value="">Select a case type...</option>
                {caseTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </div>
          </div>

          {/* Text fields */}
          {FIELD_CONFIG.map(({ key, label, required, helper, placeholder, minLength }) => (
            <div key={key}>
              <label className="block text-sm font-semibold mb-1">
                {label} {required ? <span className="text-red-500">*</span> : <span className="text-gray-600">(Optional)</span>}
              </label>
              {helper && <p className="text-xs text-gray-500 mb-2">{helper}</p>}
              <textarea
                value={form[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={placeholder}
                rows={4}
                className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/15 rounded-xl text-sm text-white focus:outline-none focus:border-red-500 resize-y min-h-[100px] placeholder:text-gray-600"
                data-testid={`field-${key}`}
              />
              {minLength && (
                <p className={`text-xs mt-1 ${form[key].length >= minLength ? "text-green-500" : "text-gray-600"}`}>
                  {form[key].length}/{minLength} min characters
                </p>
              )}
            </div>
          ))}

          {/* Consent */}
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => handleChange("consent", e.target.checked)}
              className="mt-1 accent-red-600"
              data-testid="consent-checkbox"
            />
            <p className="text-xs text-gray-500 leading-relaxed">
              By submitting this Case File, I grant Red Ink Studios exclusive, perpetual, worldwide rights to use, adapt, transform,
              and modify this content for any purpose. I also grant permission to use my submission as inspiration for derived works.
              I acknowledge that any derived property belongs to Red Ink Studios and that I will not be credited as author of any
              derived works. The 10-credit reward constitutes full compensation for this submission.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="witness-error">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="submit-case-btn"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send size={18} />
                Submit Case File — Earn 10 Credits
              </>
            )}
          </button>
        </form>
      </div>

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
}
